import PageLayout from '@/wrappers/PageLayout';
import { COACell } from '@/components/COACell';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getChangelogs,
  getChannels,
  getClientChannelAccountNumbers,
  postClientChannelAccountNumbers,
} from '@/core/api';
import { FormikErrors, useFormik } from 'formik';
import {
  activeCompanySelector,
  queryKeySelector,
} from '@/core/store/user/userSlice';
import { APIEventsEnum, useAppSelector, useSendEvent } from '@/hooks';
import { Channel, ChannelAccountNumber } from '@/core/types';
import { isCustom } from '@/components/ChartOfAccounts/ChartOfAccounts.helpers';
import { Loader } from '@/components/Loader/Loader';
import useNotification from '@/hooks/useNotification';
import { COATabHeader } from '@/components/COATabHeader';
import { useTranslation } from 'react-i18next';
import { COALastUpdated } from '@/components/ChartOfAccounts/COALastUpdated';
import { ConfirmLeaveWithoutChanges } from '@/components/ConfirmLeaveWithoutChanges/ConfirmLeaveWithoutChanges';

interface FormValues {
  [key: string]: string;
}

export default function COADebtor() {
  const { i18n, t } = useTranslation();
  const { sendEvent } = useSendEvent();

  const { showNotification } = useNotification();
  const MAPPING = i18n.language === 'en' ? MAPPING_EN : MAPPING_DE;
  const queryClient = useQueryClient();
  const activeCompany = useAppSelector(activeCompanySelector);
  const queryKey = useAppSelector(queryKeySelector);

  const { data: channels, isLoading: isLoadingChannels } = useQuery({
    queryKey: ['Channels'],
    queryFn: getChannels,
  });

  const { data: accounts, isLoading: isLoadingAccounts } = useQuery({
    queryKey: ['clientChannelAccountNumbers', queryKey],
    queryFn: () => getClientChannelAccountNumbers(activeCompany?.company_id!),
    enabled: !!activeCompany?.company_id,
  });

  const {
    data: channelsAccsChangeLogs,
    isLoading: isLoadingChannelsAccsChangeLogs,
  } = useQuery({
    queryKey: ['ChannelsAccsChangeLogs', queryKey],
    queryFn: () =>
      getChangelogs(activeCompany?.company_id || '', 'channel_booking_code'),
    enabled: !!activeCompany?.company_id,
  });

  const [accountsMap, setAccountsMap] = useState<
    Record<string, ChannelAccountNumber>
  >({});

  const [isEditing, setIsEditing] = useState(false);

  const [initialState, setInitialState] = useState<FormValues>(
    {} as FormValues
  );

  const channelsMap = useMemo(() => {
    return (channels || []).reduce(
      (acc: Record<string, Channel>, item: Channel) => {
        acc[item.name] = item;
        return acc;
      },
      {}
    );
  }, [channels]);

  const formik = useFormik<FormValues>({
    initialValues: {},
    validate: (values) => {
      const errors: FormikErrors<FormValues> = {};
      Object.entries(values).forEach(([k, v]) => {
        if (!v) {
          errors[k as keyof FormValues] = 'Required';
        } else if (v.length < 5 || v.length > 8) {
          errors[k as keyof FormValues] = 'Between 5—8 digits';
        } else if (+v <= 0) {
          errors[k as keyof FormValues] = 'Account number invalid';
        }
      });
      return errors;
    },
    onSubmit: async (values) => {
      if (!activeCompany?.company_id || !channels) return;

      const accountNumbers = Object.entries(values).map(([k, value]) => {
        const id = accountsMap[k]?.id;
        if (
          (!id &&
            channels.some(
              () => String(value) === String(channelsMap[k].booking_code)
            )) ||
          (id && String(value) === String(accountsMap[k].account_number))
        ) {
          return null;
        }

        return {
          channel: channelsMap[k].id,
          account_number: +value,
          id,
        };
      });

      try {
        const res = await postClientChannelAccountNumbers(
          activeCompany.company_id,
          accountNumbers.filter((v): v is NonNullable<typeof v> => v != null)
        );

        sendEvent(APIEventsEnum.update_chart_of_accounts, {
          tab: 'Debtor accounts',
        });

        queryClient.setQueryData(
          ['clientChannelAccountNumbers', queryKey],
          res
        );

        const changelogsRes = await getChangelogs(
          activeCompany?.company_id || '',
          'channel_booking_code'
        );

        queryClient.setQueryData(
          ['ChannelsAccsChangeLogs', queryKey],
          changelogsRes
        );

        setIsEditing(false);

        showNotification({
          text: 'Account numbers are updated successfully!',
          type: 'success',
        });
      } catch (error) {
        console.error(error);
      }
    },
  });

  useEffect(() => {
    if (channels && accounts) {
      const values = JSON.parse(JSON.stringify(formik.values));
      channels.forEach((a) => {
        values[a.name] = String(a.booking_code);
      });

      const accs = accounts.reduce(
        (acc, a) => {
          values[a.channel.name] = String(a.account_number);
          acc[a.channel.name] = a;
          return acc;
        },
        {} as Record<string, ChannelAccountNumber>
      );

      setAccountsMap(accs);
      formik.setValues(values);
      setInitialState(values);
    }
    // eslint-disable-next-line
  }, [channels, accounts]);

  if (
    isLoadingAccounts ||
    isLoadingChannels ||
    isLoadingChannelsAccsChangeLogs
  ) {
    return <Loader />;
  }

  return (
    <PageLayout
      className='flex-grow !bg-white !pt-0'
      rightColumn={<div className='max-w-[248px]'></div>}
    >
      <div>
        <COATabHeader
          title='Debtor accounts'
          onClickCancel={() => {
            formik.setValues(initialState);
            setIsEditing(false);
          }}
          onClickUpdate={() => formik.submitForm()}
          onClickEdit={() => setIsEditing(true)}
          isLoading={formik.isSubmitting}
          isEditing={isEditing}
          hasCustomAccountNumbers={!!accounts?.length}
        >
          {!!channelsAccsChangeLogs?.length && (
            <div className='text-sm text-gray-700'>
              {t('Last updated')}:{' '}
              <COALastUpdated
                date={channelsAccsChangeLogs[0].created_at}
                role={channelsAccsChangeLogs[0].role_name}
              />
            </div>
          )}
        </COATabHeader>
        <div className='mb-2 flex items-center justify-between gap-x-2 rounded-lg border px-4 pt-4'>
          <div className='pb-4 text-h4 font-bold'>{MAPPING['UNDEFINED']}</div>
          <div>
            <COACell
              value={formik.values.UNDEFINED}
              isEditing={isEditing}
              error={formik.errors.UNDEFINED || ''}
              touched={formik.touched.UNDEFINED || false}
              testId='UNDEFINED'
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name='UNDEFINED'
              isCustom={isCustom(
                formik.values.UNDEFINED,
                channelsMap.UNDEFINED.booking_code
              )}
              inputWrapperClassName={`min-h-[66px] flex items-center`}
            />
          </div>
        </div>
        <div className='mb-4 rounded-lg border p-4 pt-2'>
          <div className='pb-4 text-h4 font-bold'>{t('Channels')}</div>
          {channels
            ?.filter((c) => c.name !== 'UNDEFINED')
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((channel) => {
              return (
                <div
                  className='flex items-center justify-between gap-x-2'
                  key={channel.id}
                >
                  <div className='pb-4 font-semibold'>
                    {MAPPING[channel.name as keyof typeof MAPPING] ||
                      channel.name}
                  </div>
                  <div>
                    <COACell
                      key={channel.name}
                      value={formik.values[channel.name]}
                      isEditing={isEditing}
                      error={formik.errors[channel.name] || ''}
                      touched={formik.touched[channel.name] || false}
                      testId={channel.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      name={channel.name}
                      isCustom={isCustom(
                        formik.values[channel.name],
                        channelsMap[channel.name].booking_code
                      )}
                      inputWrapperClassName={`min-h-[66px] flex items-center`}
                    />
                  </div>
                </div>
              );
            })}
        </div>
        <ConfirmLeaveWithoutChanges
          saveChanges={formik.submitForm}
          blockCondition={
            JSON.stringify(initialState) !== JSON.stringify(formik.values)
          }
          isLoading={formik.isSubmitting}
        />
      </div>
    </PageLayout>
  );
}

const MAPPING_EN: Record<string, string> = {
  amazon: 'Amazon',
  UNDEFINED: 'Standard Account',
  ebay: 'Ebay',
  JTL: 'JTL',
  shopify: 'Shopify',
  Webshop: 'Webshop',
  Kaufland: 'Kaufland',
  SCX: 'SCX',
  shopware: 'Shopware',
  woocommerce: 'WooCommerce',
  etsy: 'Etsy',
  real: 'Real',
  Otto: 'Otto',
  custom: 'Custom',
  'XML-Import': 'Import (XML)',
  CDISCOUNT: 'Cdiscount',
  hood: 'Hood',
};

const MAPPING_DE = {
  amazon: 'Amazon',
  UNDEFINED: 'Standardkonto',
  ebay: 'Ebay',
  JTL: 'JTL',
  shopify: 'Shopify',
  Webshop: 'Webshop',
  Kaufland: 'Kaufland',
  SCX: 'SCX',
  shopware: 'Shopware',
  woocommerce: 'WooCommerce',
  etsy: 'Etsy',
  real: 'Real',
  Otto: 'Otto',
  custom: 'Individuell',
  'XML-Import': 'Import (XML)',
  CDISCOUNT: 'Cdiscount',
  hood: 'Hood',
};
